import { z } from 'zod'

// 'pending_delete' is a cancellation that has not taken effect yet: the
// registration stays live and owned until the paid period ends. 'deleted' means
// it is actually gone. 'transfer_pending' is an inbound transfer the registrar
// has not completed yet.
export const AdminDomainStatus = z.enum([
  'active',
  'pending_delete',
  'deleted',
  'trade_out',
  'external',
  'suspended',
  'transfer_pending'
])
export type AdminDomainStatus = z.infer<typeof AdminDomainStatus>

// Only contact_verification lifts itself, once the registrant contact verifies.
export const AdminSuspensionCause = z.enum(['contact_verification', 'other'])
export type AdminSuspensionCause = z.infer<typeof AdminSuspensionCause>

// clientHold at the registry, which is what stops a suspended domain
// resolving. 'failed': the registrar did not confirm it; the domain may still
// resolve.
export const AdminRegistryHold = z.enum(['not_held', 'held', 'failed'])
export type AdminRegistryHold = z.infer<typeof AdminRegistryHold>

export const AdminDomainListItem = z.object({
  id: z.number().int(),
  name: z.string(),
  name_ace: z.string(),
  status: AdminDomainStatus,
  registered: z.string().nullable().optional(),
  suspended_at: z.string().nullable().optional(),
  suspension_reason: z.string().nullable().optional(),
  suspension_cause: AdminSuspensionCause.nullable().optional(),
  registry_hold: AdminRegistryHold.optional(),
  registry_hold_error: z.string().nullable().optional(),
  account_id: z.number().int(),
  account_email: z.string()
})
export type AdminDomainListItem = z.infer<typeof AdminDomainListItem>

export const AdminHandleRef = z.object({
  id: z.number().int(),
  external_id: z.number().int().nullable().optional(),
  external_name: z.string().nullable().optional(),
  type: z.string().nullable().optional()
})
export type AdminHandleRef = z.infer<typeof AdminHandleRef>

export const AdminDomainDetails = z.object({
  id: z.number().int(),
  name: z.string(),
  name_ace: z.string(),
  external_id: z.number().int().nullable().optional(),
  status: AdminDomainStatus,
  registered: z.string().nullable().optional(),
  suspended_at: z.string().nullable().optional(),
  suspension_reason: z.string().nullable().optional(),
  suspension_cause: AdminSuspensionCause.nullable().optional(),
  registry_hold: AdminRegistryHold.optional(),
  registry_hold_error: z.string().nullable().optional(),
  subscription_id: z.number().int().nullable().optional(),
  stripe_subscription_id: z.string().nullable().optional(),
  account_id: z.number().int(),
  account_email: z.string(),
  nameservers: z.array(z.string()).optional(),
  auth_code: z.string().nullable().optional(),
  transfer_lock: z.boolean().optional(),
  uses_inwx_ns: z.boolean().optional(),
  owner_handle: AdminHandleRef.nullable().optional(),
  admin_handle: AdminHandleRef.nullable().optional(),
  subdomain_count: z.number().int().optional()
})
export type AdminDomainDetails = z.infer<typeof AdminDomainDetails>
